'use strict';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import {SelectOption} from './SelectOption';
const angular = require('angular');
const {copy} = angular;

SelectOptionsService.$inject = [
  '$parse',
  'HashMap',
];

/* eslint-disable max-len */
//                       //00001111111111000000000002222222222000000000000000000000333333333300000000000000000000000004444444444400000000000000000000005555555555500000000000006666666666666660000000007777777777777770000000888888888888888000000000000000999999999900000000000000000001010101010
const NG_OPTIONS_REGEXP = /^\s*([\s\S]+?)(?:\s+as\s+([\s\S]+?))?(?:\s+group\s+by\s+([\s\S]+?))?(?:\s+disable\s+when\s+([\s\S]+?))?(?:\s+with\s+class\s+([\s\S]+?))?\s+for\s+(?:([\$\w][\$\w]*)|(?:\(\s*([\$\w][\$\w]*)\s*,\s*([\$\w][\$\w]*)\s*\)))\s+in\s+([\s\S]+?)(?:\s+track\s+by\s+([\s\S]+?))?$/;
/* eslint-enable max-len */
// 1: value expression (valueFn)
// 2: label expression (displayFn)
// 3: group by expression (groupByFn)
// 4: disable when expression (disableWhenFn)
// 5: with class expression (classNameFn)
// 6: array item variable name
// 7: object item key variable name
// 8: object item value variable name
// 9: collection expression
// 10: track by expression

export function SelectOptionsService($parse, HashMap) {
  this.parseOptionsExpression = parseOptionsExpression;


  function parseOptionsExpression(optionsExp, scope, attrs) {

    const match = optionsExp.match(NG_OPTIONS_REGEXP);
    if (!(match)) {
      throw Error(
        "Expected expression in form of " +
        "'_select_ (as _label_)? (disable when _disable_) (with class _class_) for (_key_,)?_value_ in _collection_'" +
        " but got '" + optionsExp + "'. Attributes: ", attrs);
    }

    // Extract the parts from the ngOptions expression

    // The variable name for the value of the item in the collection
    const valueName = match[6] || match[8];
    // The variable name for the key of the item in the collection
    const keyName = match[7];

    // An expression that generates the viewValue for an option if there is a label expression
    const selectAs = / as /.test(match[0]) && match[1];
    // An expression that is used to track the id of each object in the options collection
    const trackBy = match[10];
    // An expression that generates the viewValue for an option if there is no label expression
    const valueFn = $parse(match[2] ? match[1] : valueName);
    const selectAsFn = selectAs && $parse(selectAs);
    const viewValueFn = selectAsFn || valueFn;
    const trackByFn = trackBy && $parse(trackBy);

    // Allow null attribute: nullable
    const nullable = attrs && attrs.nullable;
    const nullableFn = nullable && $parse(nullable);

    // Get the value by which we are going to track the option
    // if we have a trackFn then use that (passing scope and locals)
    // otherwise just hash the given viewValue
    const getTrackByValueFn = trackBy
      ? (value, locals) => trackByFn(scope, locals)
      : value => HashMap.hashKey(value);
    const getTrackByValue = function (value, key) {
      return getTrackByValueFn(value, getLocals(value, key));
    };

    const displayFn = $parse(match[2] || match[1]);
    const groupByFn = $parse(match[3] || '');
    const disableWhenFn = $parse(match[4] || '');
    const classNameFn = $parse(match[5] || '');
    const valuesFn = $parse(match[9]);

    const locals = {};
    const getLocals = keyName
      ? (value, key) => {
        locals[keyName] = key;
        locals[valueName] = value;
        return locals;
      } : value => {
        locals[valueName] = value;
        return locals;
      };


    return {
      trackBy: trackBy,
      getTrackByValue: getTrackByValue,
      getWatchables: $parse(valuesFn, function (values) {
        // Create a collection of things that we would like to watch (watchedArray)
        // so that they can all be watched using a single $watchCollection
        // that only runs the handler once if anything changes
        const watchedArray = [];
        values = values || [];

        if (nullableFn) {
          watchedArray.push(nullableFn(scope));
        }

        Object.keys(values).forEach(function getWatchable(key) {
          const locals = getLocals(values[key], key);
          const selectValue = getTrackByValueFn(values[key], locals);
          watchedArray.push(selectValue);

          // Only need to watch the displayFn if there is a specific label expression
          if (match[2] || match[1]) {
            const label = displayFn(scope, locals);
            watchedArray.push(label);
          }

          // Only need to watch the disableWhenFn if there is a specific disable expression
          if (match[4]) {
            const disableWhen = disableWhenFn(scope, locals);
            watchedArray.push(disableWhen);
          }

          // Only need to watch the classNameFn if there is a specific class expression
          if (match[5]) {
            const className = classNameFn(scope, locals);
            watchedArray.push(className);
          }
        });
        return watchedArray;
      }),

      getOptions: function () {

        const optionItems = [];
        const selectValueMap = {};

        // The option values were already computed in the `getWatchables` fn,
        // which must have been called to trigger `getOptions`
        const optionValues = valuesFn(scope) || [];
        let optionValuesKeys;


        if (!keyName && isArrayLike(optionValues)) {
          optionValuesKeys = optionValues;
        } else {
          // if object, extract keys, in enumeration order, unsorted
          optionValuesKeys = [];
          for (const itemKey in optionValues) {
            if (optionValues.hasOwnProperty(itemKey) && itemKey.charAt(0) !== '$') {
              optionValuesKeys.push(itemKey);
            }
          }
        }

        const optionValuesLength = optionValuesKeys.length;

        for (let index = 0; index < optionValuesLength; index++) {
          const key = (optionValues === optionValuesKeys) ? index : optionValuesKeys[index];
          const value = optionValues[key];
          const locals = getLocals(value, key);
          const viewValue = viewValueFn(scope, locals);
          const selectValue = getTrackByValueFn(viewValue, locals);
          const label = displayFn(scope, locals);
          const group = groupByFn(scope, locals);
          const disabled = disableWhenFn(scope, locals);
          const className = classNameFn(scope, locals);
          const optionItem = new SelectOption(selectValue, viewValue, label, group, disabled, className);

          optionItems.push(optionItem);
          selectValueMap[selectValue] = optionItem;
        }

        return {
          items: optionItems,
          selectValueMap: selectValueMap,
          getOptionFromViewValue: value => selectValueMap[getTrackByValue(value)],
          /* If the viewValue could be an object that may be mutated by the application,
           * we need to make a copy and not return the reference to the value on the option.
           */
          getViewValueFromOption: option => trackBy ? copy(option.viewValue) : option.viewValue,
          getNullOption: function () {
            const viewValue = null;
            const selectValue = null;
            const label = isFunction(nullableFn) ? nullableFn(scope) : null;
            const group = null;
            const disabled = false;
            const className = 'popover-select-null';
            return new SelectOption(selectValue, viewValue, label, group, disabled, className);
          },
        };
      },
    };
  }
}

// copied from angular.js source code as it does not appear to be publicly exposed.
const NODE_TYPE_ELEMENT = 1;
/**
 * @private
 * @param {*} obj
 * @return {boolean} Returns true if `obj` is an array or array-like object (NodeList, Arguments,
 *                   String ...)
 */
function isArrayLike(obj) {
  if (isNil(obj) || isWindow(obj)) {
    return false;
  }

  // Support: iOS 8.2 (not reproducible in simulator)
  // "length" in obj used to prevent JIT error (gh-11508)
  const length = "length" in Object(obj) && obj.length;

  if (obj.nodeType === NODE_TYPE_ELEMENT && length) {
    return true;
  }

  return isString(obj)
    || isArray(obj)
    || length === 0
    || isNumber(length) && length > 0 && (length - 1) in obj;
}

/**
 * Checks if `obj` is a window object.
 *
 * @private
 * @param {*} obj Object to check
 * @returns {boolean} True if `obj` is a window obj.
 */
function isWindow(obj) {
  return obj && obj.window === obj;
}



// WEBPACK FOOTER //
// ./src/js/core-components/popover-select/SelectOptionsFactory.js